import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { forwardRef, SelectHTMLAttributes } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import {
    backgroundColor,
    disabledText,
    inputBorder,
    inputBorderFocused,
    inputBorderInvalid,
    link,
    primaryText,
} from 'styles/theme';
import { unique } from 'utils/array';

export type SelectOption = {
    title: string;
    value: string | number;
    group?: string;
};

export type GroupedSelectOption = Required<SelectOption>;

type PropsFromHTMLSelect =
    | 'disabled'
    | 'name'
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'className';
export type SelectProps = Pick<
    SelectHTMLAttributes<HTMLSelectElement>,
    PropsFromHTMLSelect
> & {
    options?: SelectOption[];
    style?: React.CSSProperties;
    invalid?: boolean;
    /**
     * The placeholder text option that will be displayed initially, but not selectable in the dropdown.
     * This property disables the effect of `hideSingleOption`, since `placeholder` requires that the user
     * make a selection.
     */
    placeholder?: string;
    /**
     * Whether or not a single option should still show the dropdown menu. Defaults to `true`.
     * This property's effect is disabled by `placeholder` property, since `placeholder` adds
     * an option that forces the user to make a selection.
     */
    hideSingleOption?: boolean;
    /**
     * The background to apply to the select. Defaults to `backgroundColor`.
     */
    background?: 'transparent' | 'backgroundColor';
    /**
     * The padding to apply inside of the select. Must be a valid CSS unit. Defaults to `1rem`.
     *
     * It is preferable to use this over setting padding through CSS because this handles layout
     * shift due to the border changing size.
     */
    padding?: string;
};

/**
 * The default padding to provide around the current text inside the select.
 */
export const defaultSelectPadding = '1rem';

const dontPassProps = ['options', 'invalid', 'hideSingleOption', 'background', 'padding'];

const StyledSelect = styled.select.withConfig({
    shouldForwardProp: (p) => !dontPassProps.includes(p),
})<SelectProps>`
    cursor: pointer;
    appearance: none;
    outline: none;
    padding: ${({ padding }) => padding ?? defaultSelectPadding} 0;
    padding-left: ${({ padding }) => padding ?? defaultSelectPadding};
    border: 0;
    border-bottom: 1px solid ${inputBorder};
    color: ${primaryText};
    ${(props) =>
        props.background === 'transparent'
            ? 'background-color: transparent'
            : css`
                  background-color: ${backgroundColor};
              `};

    /* [1]: prevent layout shift of larger border width when focused / invalid */
    padding-bottom: calc(${({ padding }) => padding ?? defaultSelectPadding} + 1px);

    ${(props) => {
        // This is a hack, because styling a <select> is still hard, I'm sorry.
        // Using background images + "appearance: none" is the best way to
        // remove the dropdown arrow and put it in your own. So, here we are
        // using data URLs to embed the path data from Font Awesome into a
        // custom SVG element which then gets encoded.
        const dropdownArrow = css`
            background-image: url('data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 512">
                <g>
                    <path fill="${link(props)}" d="${faAngleDown.icon[4]}"/>
                </g>
            </svg>`)}');
            background-repeat: no-repeat, repeat;
            background-position: right 0.7em top 50%, 0 0;
            background-size: 0.65em auto, 100%;
        `;

        const noSelection = css`
            pointer-events: none;
            /* Using double ampersand to create greater specificity */
            &&,
            &&:focus {
                border-color: ${inputBorder};
            }
        `;

        if (props.options === undefined) {
            return noSelection;
        }
        if (props.options.length === 0) {
            return noSelection;
        }
        if (
            props.hideSingleOption &&
            props.placeholder === undefined &&
            props.options.length === 1
        ) {
            return noSelection;
        }

        return dropdownArrow;
    }}

    &:focus {
        border-color: ${inputBorderFocused};
        border-width: 2px;
        padding-bottom: ${({ padding }) =>
            padding ?? defaultSelectPadding}; /* see comment [1] */
    }

    &:disabled {
        color: ${disabledText};
        cursor: not-allowed;
    }

    &[aria-invalid='true'] {
        border-color: ${inputBorderInvalid};
        border-width: 2px;
        padding-bottom: ${({ padding }) =>
            padding ?? defaultSelectPadding}; /* see comment [1] */
    }
`;

function Select(
    {
        options,
        name,
        style,
        disabled,
        value,
        onChange,
        onBlur,
        className,
        invalid,
        placeholder,
        hideSingleOption = true,
        background = 'backgroundColor',
        padding,
    }: SelectProps,
    ref: React.Ref<HTMLSelectElement>
) {
    return (
        <StyledSelect
            ref={ref}
            name={name}
            style={style}
            disabled={disabled}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            aria-invalid={invalid}
            className={className}
            hideSingleOption={hideSingleOption}
            placeholder={placeholder}
            options={options}
            background={background}
            padding={padding}
        >
            {placeholder && (
                <option value="" hidden>
                    {placeholder}
                </option>
            )}
            {options?.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.title}
                </option>
            ))}
        </StyledSelect>
    );
}

const forwardRefSelect = forwardRef(Select);
export { forwardRefSelect as Select };

type GroupedSelectProps = Omit<SelectProps, 'options'> &
    Readonly<{
        /**
         * A list of [groupName, groupOptions]
         */
        optionsEntries: [string, SelectOption[]][];
    }>;

const GroupedSelect = forwardRef(
    (
        {
            optionsEntries,
            name,
            style,
            disabled,
            value,
            onChange,
            onBlur,
            className,
            invalid,
            placeholder,
            hideSingleOption = true,
            background = 'backgroundColor',
        }: GroupedSelectProps,
        ref: React.Ref<HTMLSelectElement>
    ) => (
        <StyledSelect
            ref={ref}
            name={name}
            style={style}
            disabled={disabled}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            aria-invalid={invalid}
            className={className}
            hideSingleOption={hideSingleOption}
            placeholder={placeholder}
            options={optionsEntries.flatMap(([, optList]) => optList)}
            background={background}
        >
            {placeholder && (
                <option value="" hidden>
                    {placeholder}
                </option>
            )}
            {optionsEntries.map(([groupName, groupOptions]) => (
                <optgroup key={groupName} label={groupName}>
                    {groupOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.title}
                        </option>
                    ))}
                </optgroup>
            ))}
        </StyledSelect>
    )
);

export { GroupedSelect };

export const groupedListToEntries = <TElem extends GroupedSelectOption>(
    optionsList: TElem[]
) =>
    Object.entries(
        optionsList.reduce<{ [key: string]: TElem[] }>((groupedOptions, current) => {
            const group = groupedOptions[current.group];
            group.push(current);
            return { ...groupedOptions, [current.group]: group };
        }, Object.fromEntries(unique(optionsList.map((opt) => opt.group)).map((groupName) => [groupName, []])))
    );
